// carousel/index.ts — public surface for the carousel bundle.
//
// Output is an IIFE (build-carousel-bundle.mjs) loaded as a footer <script>. It:
//   • attaches window.ftBuildCarousel = buildCarousel for ad-hoc callers, and
//   • defines + self-runs window.ftMountCarousels(root?), the placeholder scanner.
//
// Mount contract (mirrors the thumbnail mount, includes/functions/thumbnail/ft_thumbnail_mount_inc.php):
// PHP's ft_carousel_mount() emits
//   <div class="ft-carousel-mount"><script type="application/json" class="ft-carousel-blob">{…payload…}</script></div>
// The JSON-in-a-script survives innerHTML insertion (so AJAX-injected panels work). The scanner reads the
// blob, builds the carousel HTML via the shared buildCarousel() (single source of truth with the cache
// cards), replaces the placeholder's children, and explicitly inits Bootstrap on the injected markup.
//
// AJAX re-hydration is by EXPLICIT scoped re-invocation (ftMountCarousels(scope)) next to the existing
// ftMountThumbnails / ftMountRuneIcons calls — deliberately NOT a body-wide MutationObserver (same
// rationale as the thumbnail bundle).

import { buildCarousel, type CarouselSetPic } from '../shared/build-carousel';

interface CarouselData {
    domId?: string;
    name?: string;
    height?: number;
    pictures?: unknown[];
    videos?: unknown[];
    activePicId?: string | null;
    setPic?: CarouselSetPic | null;
}

declare global {
    interface Window {
        bootstrap?: {
            Carousel?: {
                getOrCreateInstance(el: Element, options: { ride: false }): unknown;
            };
        };
        ftBuildCarousel?: (data: CarouselData) => string;
        ftMountCarousels?: (root?: Element | Document) => void;
        // campaign-init bundle (post-template-mid). Wires the play/pause behavior of a
        // .video-carousel; idempotent via the .initialized class it stamps.
        initializeVideoCarousels?: () => void;
    }
}

// Per-mount counter so the container id stays unique even when PHP ships the same item_id twice on a
// gallery/standalone page (render_carousel had a latent #carouselIndicators_<item_id> collision there).
let _carSeq = 0;

// buildCarousel() taking the mount payload shape (name → itemName, pictures → pics).
export function ftBuildCarousel(data: CarouselData): string {
    return buildCarousel({
        domId: String(data.domId ?? '') || `c${_carSeq}`,
        itemName: String(data.name ?? ''),
        pics: Array.isArray(data.pictures) ? data.pictures : [],
        videos: Array.isArray(data.videos) ? data.videos : [],
        height: typeof data.height === 'number' ? data.height : undefined,
        activePicId: data.activePicId ?? null,
        setPic: data.setPic ?? null,
    });
}

// Bootstrap 5 drives prev/next/indicators through a delegated document click handler, so injected
// markup is navigable without init; but ride/touch/keyboard wiring needs an instance — create one
// (typeof-guarded; no-op if Bootstrap or the element is absent). ride:false → no autoplay (matches PHP).
function initBootstrapCarousel(host: HTMLElement): void {
    try {
        const bs = window.bootstrap;
        if (!bs || !bs.Carousel) return;
        const el = host.querySelector('.carousel');
        if (el) bs.Carousel.getOrCreateInstance(el, { ride: false });
    } catch (e) {
        // Non-fatal: the delegated click handler still navigates the carousel.
    }
}

// Scan `root` (default document) for un-mounted carousel placeholders, render each from its JSON blob.
export function ftMountCarousels(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('.ft-carousel-mount:not([data-carousel-mounted])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-carousel-mounted', '1'); // mark first so a thrown render never loops on rescan
        const blob = el.querySelector('script.ft-carousel-blob');
        if (!blob) continue;
        try {
            const data = JSON.parse(blob.textContent || 'null') as CarouselData | null;
            if (!data) continue;
            // Force a per-mount-unique domId so two mounts of the same item never share a container id.
            data.domId = `${String(data.domId ?? '')}_m${++_carSeq}`;
            const html = ftBuildCarousel(data);
            el.innerHTML = html; // drops the JSON script; '' (no media) leaves the mount empty (graceful)
            if (html !== '') initBootstrapCarousel(el);
        } catch (e) {
            console.warn('ftMountCarousels: mount failed', e);
        }
    }
    ensureVideoCarouselsInitialized(scope);
}

// A video carousel's <video> ships with NO autoplay attribute (build-carousel.ts) — playback is
// owned entirely by initializeVideoCarousels() (visibility + hover driven, .initialized-guarded).
// That runs once from loadPostFunctions at PAGE LOAD, so any carousel born from a LATER ajax inject
// got its markup but never its playback wiring, and the video sat forever on a blank
// preload="metadata" frame. That was the class page's subclass panel (querySubclassTable →
// table_subclass_from_class_inc → the object card's own video header): picker correct, card correct,
// video dead.
//
// Every inject path already calls ftMountCarousels, so wiring it HERE fixes them all at once — and it
// deliberately scans the whole scope rather than only the placeholders this pass painted, because the
// object-card / creature-card / player-card renderers build their video header INLINE via
// buildCarousel() (no .ft-carousel-mount placeholder) and then call ftMountCarousels(container) for
// their nested mounts. Scoped scan → those inline headers get wired too.
// The scoped check is a cheap gate on a document-wide call, so a miss only costs a skipped init —
// but querySelector never matches the ROOT itself, so a caller that hands us the carousel element
// as its scope would fall through it. Match the root explicitly first.
function ensureVideoCarouselsInitialized(scope: Element | Document): void {
    try {
        if (typeof window.initializeVideoCarousels !== 'function') return; // campaign-init not loaded yet (page load covers it)
        const rootIsCarousel = typeof (scope as Element).matches === 'function'
            && (scope as Element).matches('.video-carousel:not(.initialized)');
        if (!rootIsCarousel && !scope.querySelector('.video-carousel:not(.initialized)')) return;
        window.initializeVideoCarousels();
    } catch (e) {
        console.warn('ftMountCarousels: video carousel init failed', e);
    }
}

window.ftBuildCarousel = ftBuildCarousel;
window.ftMountCarousels = ftMountCarousels;

// Render page-load mounts as soon as the bundle executes (footer <script>, so every mount above it is
// parsed), and again on DOMContentLoaded for any that came after. The data-carousel-mounted flag makes
// the second pass a no-op on already-rendered placeholders.
if (document.readyState !== 'loading') {
    ftMountCarousels();
} else {
    document.addEventListener('DOMContentLoaded', () => ftMountCarousels());
}
